import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Phone, Trash2, CheckCircle2, AlertCircle } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

interface CredentialSummary {
  provider: string;
  configuredAt: string;
  lastVerifiedAt: string | null;
  lastError: string | null;
}

const PROVIDER_FIELDS: Record<string, Array<{ key: string; label: string; type?: string }>> = {
  TWILIO: [
    { key: 'account_sid', label: 'Account SID' },
    { key: 'auth_token', label: 'Auth token', type: 'password' },
  ],
  VONAGE: [
    { key: 'api_key', label: 'API key' },
    { key: 'api_secret', label: 'API secret', type: 'password' },
  ],
  TELNYX: [{ key: 'api_key', label: 'API key', type: 'password' }],
};

const PROVIDERS = ['TWILIO', 'VONAGE', 'TELNYX'] as const;

/**
 * Story 51.3 — admin page for per-tenant carrier credentials. Tenant
 * admins enter their own Twilio / Vonage / Telnyx keys here; the
 * VoiceProvider classes pick them up at call time and fall back to the
 * deploy-wide @Value config if a tenant hasn't configured anything.
 */
export function VoiceCredentialsPage() {
  const { t } = useTranslation();
  const [summaries, setSummaries] = useState<CredentialSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get<CredentialSummary[]>('/voice/credentials')
      .then(({ data }) => setSummaries(data))
      .catch(() => setSummaries([]))
      .finally(() => setLoading(false));
  }, [refreshToken]);

  function startEdit(provider: string) {
    setEditing(provider);
    setDraft({});
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.put(`/voice/credentials/${editing}`, { fields: draft });
      setEditing(null);
      setDraft({});
      setRefreshToken((x) => x + 1);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('voiceCreds.error_save'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(provider: string) {
    try {
      await apiClient.delete(`/voice/credentials/${provider}`);
      setRefreshToken((x) => x + 1);
    } catch {
      setError(t('voiceCreds.error_remove'));
    }
  }

  const summaryByProvider = new Map(summaries.map((s) => [s.provider, s]));

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center gap-3">
        <Phone className="h-5 w-5 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold">{t('voiceCreds.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('voiceCreds.subtitle')}</p>
        </div>
      </header>

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {PROVIDERS.map((provider) => {
            const summary = summaryByProvider.get(provider);
            const isEditing = editing === provider;
            return (
              <Card key={provider} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">{provider}</h2>
                  {summary ? (
                    <span className="rounded-full bg-emerald-900/40 px-2 py-0.5 text-xs font-medium text-emerald-200">
                      {t('voiceCreds.status_configured')}
                    </span>
                  ) : (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {t('voiceCreds.status_unconfigured')}
                    </span>
                  )}
                </div>

                {summary && !isEditing && (
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div>
                      {t('voiceCreds.configured_at', {
                        date: new Date(summary.configuredAt).toLocaleDateString(),
                        defaultValue: 'Configured: {{date}}',
                      })}
                    </div>
                    {summary.lastVerifiedAt ? (
                      <div className="flex items-center gap-1 text-emerald-300">
                        <CheckCircle2 className="h-3 w-3" />
                        {t('voiceCreds.verified_at', {
                          date: new Date(summary.lastVerifiedAt).toLocaleDateString(),
                          defaultValue: 'Last verified: {{date}}',
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-amber-300">
                        <AlertCircle className="h-3 w-3" />
                        {t('voiceCreds.never_verified')}
                      </div>
                    )}
                    {summary.lastError && (
                      <div className="text-rose-300">{summary.lastError}</div>
                    )}
                  </div>
                )}

                {isEditing ? (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    {(PROVIDER_FIELDS[provider] ?? []).map((field) => (
                      <div key={field.key} className="space-y-1">
                        <label className="text-xs font-medium">{field.label}</label>
                        <Input
                          type={field.type ?? 'text'}
                          value={draft[field.key] ?? ''}
                          onChange={(e) =>
                            setDraft({ ...draft, [field.key]: e.target.value })
                          }
                          required
                        />
                      </div>
                    ))}
                    {error && (
                      <div className="rounded-md border border-red-800 bg-red-950/40 px-2 py-1.5 text-xs text-red-300">
                        {error}
                      </div>
                    )}
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditing(null);
                          setDraft({});
                        }}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={
                          submitting ||
                          (PROVIDER_FIELDS[provider] ?? []).some(
                            (f) => !(draft[f.key] ?? '').trim(),
                          )
                        }
                      >
                        {submitting ? t('common.saving') : t('common.save')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex justify-end gap-2">
                    {summary && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemove(provider)}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        {t('voiceCreds.remove')}
                      </Button>
                    )}
                    <Button size="sm" onClick={() => startEdit(provider)}>
                      {summary ? t('voiceCreds.replace') : t('voiceCreds.configure')}
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t('voiceCreds.security_note')}</p>
    </div>
  );
}
